import axios from "axios"
import {
  clearAuthToken,
  getAuthToken,
} from "@/lib/storage"

/** В dev — прокси Vite; в prod — VITE_API_BASE или backend.daks.by */
export function resolveApiBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_API_BASE?.trim()
  if (fromEnv) {
    return fromEnv.endsWith("/") ? fromEnv : `${fromEnv}/`
  }
  if (import.meta.env.DEV) {
    return "/"
  }
  return "https://backend.daks.by/"
}

export const api = axios.create({
  baseURL: resolveApiBaseUrl(),
})

api.interceptors.request.use((config) => {
  const token = getAuthToken()
  if (token) {
    config.headers.Authorization = token
  }
  return config
})

let logoutHandler: (() => void | Promise<void>) | null = null
let handlingUnauthorized401 = false

export function setLogoutHandler(handler: (() => void | Promise<void>) | null) {
  logoutHandler = handler
}

function normalizeRequestPath(config: { url?: string } | undefined): string {
  const raw = String(config?.url ?? "").trim()
  if (!raw) return ""
  try {
    const pathOnly = raw.startsWith("http")
      ? raw.replace(/^https?:\/\/[^/]+/i, "")
      : raw
    return pathOnly.replace(/^\//, "").split("?")[0]
  } catch {
    return ""
  }
}

function isAuthHandshakePath(path: string): boolean {
  return (
    path === "auth/send" ||
    path === "auth/login" ||
    path.startsWith("auth/send/") ||
    path.startsWith("auth/login/")
  )
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status
    if (status !== 401 || !logoutHandler) {
      return Promise.reject(error)
    }

    const path = normalizeRequestPath(error?.config)
    if (isAuthHandshakePath(path)) {
      return Promise.reject(error)
    }

    if (!getAuthToken()) {
      return Promise.reject(error)
    }

    if (handlingUnauthorized401) {
      return Promise.reject(error)
    }

    handlingUnauthorized401 = true
    try {
      await logoutHandler()
    } finally {
      handlingUnauthorized401 = false
    }
    return Promise.reject(error)
  }
)

export function clearSessionOnClient() {
  clearAuthToken()
}
